import { FlightFilterCategories } from '@pages/FlightSearchResult/components/FlightFilters/type';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { cloneDeep, remove } from 'lodash-es';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useFlightContext } from '@context/FlightContext';

import { notUndefinedAndNull } from '@helpers/array';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

type FlightSearchFiltersProviderValue = {
  pricesFilter: { leg: number; values: number[] }[];
  departDurationsFilter: { leg: number; values: number[] }[];
  returnDurationsFilter: number[];
  stopoverDurationsFilter: { leg: number; values: number[] }[];
  departLegDepartureTimeBlocksFilter: { leg: number; values: FlightFilterTimeBlock[] }[];
  departLegArrivalTimeBlocksFilter: { leg: number; values: FlightFilterTimeBlock[] }[];
  returnLegDepartureTimeBlocksFilter: { leg: number; values: FlightFilterTimeBlock[] }[];
  returnLegArrivalTimeBlocksFilter: { leg: number; values: FlightFilterTimeBlock[] }[];
  airlineCodesFilter: { leg: number; values: string[] }[];
  stopCodesFilter: { leg: number; values: number[] }[];
  providerCodesFilter: { leg: number; values: string[] }[];
  flightExperiencesFilter: { leg: number; values: string[] }[];
  allianceCodesFilter: { leg: number; values: string[] }[];
  tripsFilter: string[];
  originAirportCodesFilter: { leg: number; values: string[] }[];
  destinationAirportCodesFilter: { leg: number; values: string[] }[];
  stopoverAirportCodesFilter: { leg: number; values: string[] }[];
  stopoverOptionsFilter: { leg: number; values: string[] }[];
  providersFilter: { leg: number; values: string[] }[];
  paymentMethodIdsFilter: { leg: number; values: number[] }[];
  fallbackFilter: { leg: number; values: any[] }[];
  toggleSingleFilter: <TValue extends string | number = string>(params: {
    searchParamKey: string;
    value: TValue;
  }) => TValue[];
  toggleMultiCitySingleFilter: <T extends string | number = string>(params: {
    activeLeg: number;
    searchParamKey: string;
    value: T | string[];
  }) => { leg: number; values: T[] }[];
  changeMultiCityRangeFilter: (params: {
    activeLeg: number;
    searchParamKey: string;
    range: number[];
    min?: number;
    max?: number;
  }) => string[];
  selectAllFilterOptions: <TActiveLeg = undefined>(params: {
    searchParamKey: FlightSearchResultSearchParams;
    filterOptions: FlightMetaSearchFilterOption[] | FlightMetaSearchMergedAirlinesFilter[];
    activeLeg?: TActiveLeg;
  }) => TActiveLeg extends number
    ? {
        leg: number;
        values: string[];
      }[]
    : string[];
  clearFilter: (params: { searchParamKey: FlightSearchResultSearchParams; activeLeg?: number }) => {
    leg: number;
    values: string[];
  }[];
  activeLeg: number;
  setActiveLeg: React.Dispatch<React.SetStateAction<number>>;
  selectedLegs: { index: number; legId: string }[];
  setSelectedLegs: React.Dispatch<React.SetStateAction<{ index: number; legId: string }[]>>;
  hasFilter: boolean;
  appliedFilters: FlightFilterCategories[];
};

export const FlightSearchFiltersContext = createContext<FlightSearchFiltersProviderValue>(
  null as unknown as FlightSearchFiltersProviderValue,
);

export type FlightSearchFiltersProviderProps = {
  children: React.ReactNode;
};

const FlightSearchFiltersProvider = ({ children }: FlightSearchFiltersProviderProps) => {
  const { isFlightSearch2 } = useFlightContext();

  const [activeLeg, setActiveLeg] = useState(0);
  const [selectedLegs, setSelectedLegs] = useState<{ index: number; legId: string }[]>([]);

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const pricesSearchParam = searchParams.get(FlightSearchResultSearchParams.Price);
  const departDurationParam = searchParams.get(FlightSearchResultSearchParams.DepartDuration);
  const returnDurationParam = searchParams.get(FlightSearchResultSearchParams.ReturnDuration);
  const stopoverDurationParam = searchParams.get(FlightSearchResultSearchParams.StopoverDuration);
  const departLegDepartureTimeBlocksParam = searchParams.get(
    FlightSearchResultSearchParams.DepartLegDepartureTimeBlocks,
  );
  const departLegArrivalTimeBlocksParam = searchParams.get(
    FlightSearchResultSearchParams.DepartLegArrivalTimeBlocks,
  );
  const returnLegDepartureTimeBlocksParam = searchParams.get(
    FlightSearchResultSearchParams.ReturnLegDepartureTimeBlocks,
  );
  const returnLegArrivalTimeBlocksParam = searchParams.get(
    FlightSearchResultSearchParams.ReturnLegArrivalTimeBlocks,
  );
  const airlinesParam = searchParams.get(FlightSearchResultSearchParams.Airlines);
  const stopCodeParam = searchParams.get(FlightSearchResultSearchParams.Stops);
  const providerCodesParam = searchParams.get(FlightSearchResultSearchParams.ProviderCodes);
  const experienceParam = searchParams.get(FlightSearchResultSearchParams.Experience);
  const tripOptionsParam = searchParams.get(FlightSearchResultSearchParams.TripOptions);
  const alliancesParam = searchParams.get(FlightSearchResultSearchParams.Alliances);
  const originParam = searchParams.get(FlightSearchResultSearchParams.Origin);
  const destinationParam = searchParams.get(FlightSearchResultSearchParams.Destination);
  const stopoversParam = searchParams.get(FlightSearchResultSearchParams.Stopovers);
  const stopoverOptionsParam = searchParams.get(FlightSearchResultSearchParams.StopoverOptions);
  const providersParam = searchParams.get(FlightSearchResultSearchParams.Providers);
  const selectedPaymentMethodIdsSearchParam = searchParams.get(
    FlightSearchResultSearchParams.SelectedPaymentMethod,
  );

  const { legs, isRoundTrip } = useFlightSearchPageParams();

  const getMultiLegsFilter = useCallback(
    <TValue extends string | number = string>(searchParamValue?: string | null) =>
      searchParamValue
        ?.split(':')
        .map((item, index) =>
          item
            ? {
                leg: index,
                values: (item
                  .split(',')
                  .map((value) => (isNaN(Number(value)) ? value : parseFloat(value))) ??
                  []) as TValue[],
              }
            : undefined,
        )
        .filter(notUndefinedAndNull) ?? [],
    [],
  );

  const fallbackFilter = useMemo(
    () => legs.map((_, index) => ({ leg: index, values: [] })),
    [legs],
  );

  const paymentMethodIdsFilter: {
    leg: number;
    values: number[];
  }[] = useMemo(
    () => getMultiLegsFilter(selectedPaymentMethodIdsSearchParam),
    [selectedPaymentMethodIdsSearchParam, getMultiLegsFilter],
  );

  const providersFilter = useMemo(
    () => getMultiLegsFilter(providersParam),
    [providersParam, getMultiLegsFilter],
  );

  const pricesFilter = useMemo(
    () => getMultiLegsFilter<number>(pricesSearchParam),
    [pricesSearchParam, getMultiLegsFilter],
  );

  const allianceCodesFilter = useMemo(
    () => getMultiLegsFilter(alliancesParam),
    [alliancesParam, getMultiLegsFilter],
  );

  const tripsFilter = useMemo(() => tripOptionsParam?.split(',') ?? [], [tripOptionsParam]);

  const originAirportCodesFilter = useMemo(
    () => getMultiLegsFilter(originParam),
    [originParam, getMultiLegsFilter],
  );

  const destinationAirportCodesFilter = useMemo(
    () => getMultiLegsFilter(destinationParam),
    [destinationParam, getMultiLegsFilter],
  );

  const stopoverOptionsFilter = useMemo(
    () => getMultiLegsFilter(stopoverOptionsParam),
    [stopoverOptionsParam, getMultiLegsFilter],
  );

  const providerCodesFilter = useMemo(
    () => getMultiLegsFilter(providerCodesParam),
    [providerCodesParam, getMultiLegsFilter],
  );

  // Return duration only used in round trip
  const returnDurationsFilter = useMemo(
    () => returnDurationParam?.split(',').map((v) => parseInt(v)) ?? [],
    [returnDurationParam],
  );

  const departDurationsFilter = useMemo(
    () => getMultiLegsFilter<number>(departDurationParam),
    [departDurationParam, getMultiLegsFilter],
  );

  const stopoverDurationsFilter = useMemo(
    () => getMultiLegsFilter<number>(stopoverDurationParam),
    [getMultiLegsFilter, stopoverDurationParam],
  );

  const flightExperiencesFilter = useMemo(
    () => getMultiLegsFilter(experienceParam),
    [experienceParam, getMultiLegsFilter],
  );

  const departLegDepartureTimeBlocksFilter = useMemo(
    () => getMultiLegsFilter<FlightFilterTimeBlock>(departLegDepartureTimeBlocksParam),
    [departLegDepartureTimeBlocksParam, getMultiLegsFilter],
  );

  const departLegArrivalTimeBlocksFilter = useMemo(
    () => getMultiLegsFilter<FlightFilterTimeBlock>(departLegArrivalTimeBlocksParam),
    [departLegArrivalTimeBlocksParam, getMultiLegsFilter],
  );

  const returnLegDepartureTimeBlocksFilter = useMemo(
    () => getMultiLegsFilter<FlightFilterTimeBlock>(returnLegDepartureTimeBlocksParam),
    [getMultiLegsFilter, returnLegDepartureTimeBlocksParam],
  );

  const returnLegArrivalTimeBlocksFilter = useMemo(
    () => getMultiLegsFilter<FlightFilterTimeBlock>(returnLegArrivalTimeBlocksParam),
    [getMultiLegsFilter, returnLegArrivalTimeBlocksParam],
  );

  const stopCodesFilter = useMemo(
    () => getMultiLegsFilter<number>(stopCodeParam),
    [getMultiLegsFilter, stopCodeParam],
  );

  const airlineCodesFilter = useMemo(
    () => getMultiLegsFilter(airlinesParam),
    [airlinesParam, getMultiLegsFilter],
  );

  const stopoverAirportCodesFilter = useMemo(
    () => getMultiLegsFilter(stopoversParam),
    [getMultiLegsFilter, stopoversParam],
  );

  const appliedFilters = useMemo(() => {
    const appliedFilters: FlightFilterCategories[] = [];

    if (paymentMethodIdsFilter.find((item) => item.leg === activeLeg)?.values.length) {
      appliedFilters.push(FlightFilterCategories.PaymentMethod);
    }

    if (stopCodesFilter.find((item) => item.leg === activeLeg)?.values.length) {
      appliedFilters.push(FlightFilterCategories.Stops);
    }

    if (providersFilter.find((item) => item.leg === activeLeg)?.values.length) {
      appliedFilters.push(FlightFilterCategories.BookingOptions);
    }

    if (
      pricesFilter.find((item) => item.leg === (isFlightSearch2 && isRoundTrip ? activeLeg : 0))
        ?.values.length
    ) {
      appliedFilters.push(FlightFilterCategories.Price);
    }

    if (flightExperiencesFilter.find((item) => item.leg === activeLeg)?.values.length) {
      appliedFilters.push(FlightFilterCategories.Experience);
    }

    let isFlightTimesFilterApplied = false;

    if (isRoundTrip && isFlightSearch2 && activeLeg === 1) {
      isFlightTimesFilterApplied = !!(
        returnLegDepartureTimeBlocksFilter.length || returnLegArrivalTimeBlocksFilter.length
      );
    } else if (isRoundTrip) {
      isFlightTimesFilterApplied = !!(
        departLegDepartureTimeBlocksFilter.length ||
        departLegArrivalTimeBlocksFilter.length ||
        returnLegDepartureTimeBlocksFilter.length ||
        returnLegArrivalTimeBlocksFilter.length
      );
    } else {
      isFlightTimesFilterApplied = !!(
        departLegDepartureTimeBlocksFilter.find((item) => item.leg === activeLeg)?.values.length ||
        departLegArrivalTimeBlocksFilter.find((item) => item.leg === activeLeg)?.values.length
      );
    }

    if (isFlightTimesFilterApplied) {
      appliedFilters.push(FlightFilterCategories.Times);
    }

    if (
      !!(
        departDurationsFilter.find((item) => item.leg === activeLeg)?.values.length ||
        stopoverDurationsFilter.find((item) => item.leg === activeLeg)?.values.length ||
        (isRoundTrip && !isFlightSearch2 && returnDurationsFilter.length)
      )
    ) {
      appliedFilters.push(FlightFilterCategories.Duration);
    }

    if (
      !!(
        airlineCodesFilter.find((item) => item.leg === activeLeg)?.values.length ||
        allianceCodesFilter.find((item) => item.leg === activeLeg)?.values.length ||
        tripsFilter.length
      )
    ) {
      appliedFilters.push(FlightFilterCategories.Airlines);
    }

    if (
      !!(
        originAirportCodesFilter.find((item) => item.leg === activeLeg)?.values.length ||
        destinationAirportCodesFilter.find((item) => item.leg === activeLeg)?.values.length
      )
    ) {
      appliedFilters.push(FlightFilterCategories.OriginAndDestination);
    }

    if (
      !!(
        stopoverOptionsFilter.find((item) => item.leg === activeLeg)?.values.length ||
        stopoverAirportCodesFilter.find((item) => item.leg === activeLeg)?.values.length
      )
    ) {
      appliedFilters.push(FlightFilterCategories.Transit);
    }

    if (!!providerCodesFilter.find((item) => item.leg === activeLeg)?.values.length) {
      appliedFilters.push(FlightFilterCategories.BookingSites);
    }

    return appliedFilters;
  }, [
    activeLeg,
    isRoundTrip,
    paymentMethodIdsFilter,
    stopCodesFilter,
    providersFilter,
    pricesFilter,
    flightExperiencesFilter,
    departLegDepartureTimeBlocksFilter,
    departLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    returnLegArrivalTimeBlocksFilter,
    departDurationsFilter,
    returnDurationsFilter,
    stopoverDurationsFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    tripsFilter,
    originAirportCodesFilter,
    destinationAirportCodesFilter,
    stopoverOptionsFilter,
    stopoverAirportCodesFilter,
    providerCodesFilter,
  ]);

  const toggleSingleFilter = useCallback(
    <TValue extends string | number = string>({
      searchParamKey,
      value,
    }: {
      searchParamKey: string;
      value: TValue;
    }) => {
      const currentFilter = (searchParams.get(searchParamKey)?.split(',') ?? []).map((v) =>
        isNaN(Number(v)) ? v : parseInt(v.toString()),
      ) as TValue[];

      const newFilter = currentFilter.includes(value)
        ? currentFilter.filter((v) => v !== value)
        : [...currentFilter, value];

      if (newFilter.length > 0) {
        searchParams.set(searchParamKey, newFilter.join(','));
      } else {
        searchParams.delete(searchParamKey);
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );

      return newFilter;
    },
    [searchParams, navigate, location.pathname],
  );

  const toggleMultiCitySingleFilter = useCallback(
    <T extends string | number>({
      activeLeg,
      searchParamKey,
      value,
    }: {
      activeLeg: number;
      searchParamKey: string;
      value: T | string[];
    }) => {
      const currentFilter = getMultiLegsFilter<T>(searchParams.get(searchParamKey));

      const allLegsFilterValues = legs
        .map((_, index) => ({
          leg: index,
          values: currentFilter.find((filter) => filter.leg === index)?.values ?? [],
        }))
        .map(({ values }) => values);

      const activeLegFilterValues = allLegsFilterValues[activeLeg];

      if (typeof value === 'string' || typeof value === 'number') {
        if (activeLegFilterValues.includes(value)) {
          remove(activeLegFilterValues, (item) => item === value);
        } else {
          activeLegFilterValues.push(value);
        }
      } else {
        const isSelected = !value.every((code) => activeLegFilterValues.includes(code as T));

        if (isSelected) {
          activeLegFilterValues.push(...([value].flat() as T[]));
        } else {
          remove(activeLegFilterValues, (item) => value.includes(item as string));
        }
      }

      const newFilter = allLegsFilterValues.map((values, index) => ({
        leg: index,
        values,
      }));

      const isNewFilterEmpty = newFilter.every((filter) => filter.values.length === 0);

      if (isNewFilterEmpty) {
        searchParams.delete(searchParamKey);
      } else {
        searchParams.set(
          searchParamKey,
          newFilter.map((leg) => (leg.values.length > 0 ? leg.values.join(',') : '')).join(':'),
        );
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );

      return newFilter;
    },
    [getMultiLegsFilter, searchParams, legs, navigate, location.pathname],
  );

  const changeMultiCityRangeFilter = useCallback(
    ({
      max,
      min,
      searchParamKey,
      range,
      activeLeg,
    }: {
      activeLeg: number;
      searchParamKey: string;
      range: number[];
      min?: number;
      max?: number;
    }) => {
      const searchParamValue = searchParams.get(searchParamKey);
      const currentFilter = searchParamValue?.split(':') || [];

      const allLegsFilter = legs.map((_, index) => currentFilter[index]);

      if (!(range[0] === min && range[1] === max)) {
        allLegsFilter[activeLeg] = range.join(',');
      } else {
        allLegsFilter[activeLeg] = '';
      }

      if (allLegsFilter.every((leg) => !leg)) {
        searchParams.delete(searchParamKey);
      } else {
        searchParams.set(searchParamKey, allLegsFilter.join(':'));
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );

      return allLegsFilter;
    },
    [searchParams, legs, navigate, location.pathname],
  );

  const selectAllFilterOptions = useCallback(
    <TActiveLeg,>({
      searchParamKey,
      filterOptions,
      activeLeg,
    }: {
      searchParamKey: FlightSearchResultSearchParams;
      filterOptions: FlightMetaSearchFilterOption[] | FlightMetaSearchMergedAirlinesFilter[];
      activeLeg?: TActiveLeg;
    }): TActiveLeg extends number
      ? {
          leg: number;
          values: string[];
        }[]
      : string[] => {
      if (typeof activeLeg !== 'number') {
        searchParams.set(
          searchParamKey,
          filterOptions.flatMap((filterOption) => filterOption.name).join(','),
        );

        navigate(
          {
            pathname: location.pathname,
            search: searchParams.toString(),
          },
          { replace: true },
        );

        return filterOptions.flatMap((filterOption) => filterOption.name) as any;
      }

      const currentFiler = searchParams.get(searchParamKey)?.split(':') ?? [];
      const newFilter = cloneDeep(currentFiler);

      const allFilterOptionValues = filterOptions.map((filterOption) => filterOption.name);

      newFilter[activeLeg] = allFilterOptionValues.join(',');

      const newSearchParamValue = newFilter.join(':');

      searchParams.set(searchParamKey, newSearchParamValue);

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );

      return getMultiLegsFilter(newSearchParamValue) as any;
    },
    [searchParams, navigate, getMultiLegsFilter, location.pathname],
  );

  const clearFilter = useCallback(
    ({
      searchParamKey,
      activeLeg,
    }: {
      searchParamKey: FlightSearchResultSearchParams;
      activeLeg?: number;
    }) => {
      if (typeof activeLeg !== 'number') {
        searchParams.delete(searchParamKey);

        navigate(
          {
            pathname: location.pathname,
            search: searchParams.toString(),
          },
          { replace: true },
        );

        return [];
      }

      const currentFilter = searchParams.get(searchParamKey)?.split(':') ?? [];
      const allLegsFilter = legs.map((_, index) => currentFilter[index]);

      allLegsFilter[activeLeg] = '';

      const newSearchParamValue = allLegsFilter.some(Boolean)
        ? allLegsFilter.map((legFilter) => legFilter ?? '').join(':')
        : '';

      if (newSearchParamValue) {
        searchParams.set(searchParamKey, newSearchParamValue);
      } else {
        searchParams.delete(searchParamKey);
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );

      return getMultiLegsFilter(newSearchParamValue);
    },
    [searchParams, legs, navigate, location.pathname, getMultiLegsFilter],
  );

  const hasFilter = Object.values(FlightSearchResultSearchParams).some((filter) =>
    searchParams.has(filter),
  );

  const providerValue = useMemo<FlightSearchFiltersProviderValue>(
    () => ({
      pricesFilter,
      departDurationsFilter,
      returnDurationsFilter,
      stopoverDurationsFilter,
      departLegDepartureTimeBlocksFilter,
      departLegArrivalTimeBlocksFilter,
      returnLegDepartureTimeBlocksFilter,
      returnLegArrivalTimeBlocksFilter,
      airlineCodesFilter,
      stopCodesFilter,
      providerCodesFilter,
      flightExperiencesFilter,
      allianceCodesFilter,
      tripsFilter,
      originAirportCodesFilter,
      destinationAirportCodesFilter,
      stopoverAirportCodesFilter,
      stopoverOptionsFilter,
      providersFilter,
      paymentMethodIdsFilter,
      fallbackFilter,
      toggleSingleFilter,
      toggleMultiCitySingleFilter,
      changeMultiCityRangeFilter,
      selectAllFilterOptions,
      clearFilter,
      activeLeg,
      setActiveLeg,
      selectedLegs,
      setSelectedLegs,
      hasFilter,
      appliedFilters,
    }),
    [
      pricesFilter,
      departDurationsFilter,
      returnDurationsFilter,
      stopoverDurationsFilter,
      departLegDepartureTimeBlocksFilter,
      departLegArrivalTimeBlocksFilter,
      returnLegDepartureTimeBlocksFilter,
      returnLegArrivalTimeBlocksFilter,
      airlineCodesFilter,
      stopCodesFilter,
      providerCodesFilter,
      flightExperiencesFilter,
      allianceCodesFilter,
      tripsFilter,
      originAirportCodesFilter,
      destinationAirportCodesFilter,
      stopoverAirportCodesFilter,
      stopoverOptionsFilter,
      providersFilter,
      paymentMethodIdsFilter,
      fallbackFilter,
      toggleSingleFilter,
      toggleMultiCitySingleFilter,
      changeMultiCityRangeFilter,
      selectAllFilterOptions,
      clearFilter,
      activeLeg,
      setActiveLeg,
      selectedLegs,
      setSelectedLegs,
      hasFilter,
      appliedFilters,
    ],
  );

  return (
    <FlightSearchFiltersContext.Provider value={providerValue}>
      {children}
    </FlightSearchFiltersContext.Provider>
  );
};

export default FlightSearchFiltersProvider;
